import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getPersisted, PersistKeys, setPersisted } from "./persistence";

interface SavedRoute {
  pathname: string;
}

const PERSISTABLE_PREFIXES = ["/members"];

function isPersistable(pathname: string) {
  return PERSISTABLE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/**
 * Keeps the "last meaningful route" in localStorage so that when the Chrome
 * extension iframe is destroyed (side panel close) and recreated on reopen,
 * we can drop the user back where they were instead of resetting to `/`.
 *
 * PHI constraint: only the pathname is persisted. Router `location.state`
 * can carry search filters (first/last name, MPI, DOB) when navigating from
 * results to a profile — that data must never land in localStorage. The
 * destination route fetches member data fresh via GraphQL on mount, so
 * dropping state has no functional cost for the reopen flow.
 */
export function RoutePersister() {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  // One-shot restore on mount.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const saved = getPersisted<SavedRoute | null>(PersistKeys.lastRoute, null);
    if (!saved?.pathname) return;

    // Deliberately NOT including "/home". Home is the app's default landing
    // page, and a boot straight to /home (bookmark, deep link, post-login
    // redirect) must STAY on Home. The extension-reopen flow this restore
    // exists for boots the iframe at "/", which is already covered below.
    const atRoot = location.pathname === "/" || location.pathname === "/members";
    if (!atRoot) return;
    if (saved.pathname === location.pathname) return;
    // Only a member PROFILE is worth restoring. A bare "/members" is an empty
    // search page — filters are never persisted (PHI, above) — and restoring
    // it would boot the user past Home onto it. Home carries the same search
    // card, so staying put loses nothing.
    if (saved.pathname === "/members") return;

    navigate(saved.pathname, { replace: true });
    // Intentionally mount-only — deps list omitted on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on every persistable location change.
  useEffect(() => {
    if (!isPersistable(location.pathname)) return;
    setPersisted<SavedRoute>(PersistKeys.lastRoute, {
      pathname: location.pathname,
    });
  }, [location.pathname]);

  return null;
}
